import { AudioPlayerOptions } from "../../models/audio";
import { AudioBasePlayer } from "./audio-base-player";

/**
 * `decodeAudioData` does not work on iOS 11.x: https://github.com/mdn/webaudio-examples/issues/5
 * and OGG format is not supported on Safari: https://caniuse.com/#feat=ogg-vorbis
 *
 * Help in case createGain is required:
 * https://github.com/cwilso/webkitAudioContext-MonkeyPatch/blob/gh-pages/webkitAudioContextMonkeyPatch.js#L178
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/decodeAudioData
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/createBufferSource
 */
export class AudioDecoderPlayer extends AudioBasePlayer {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  // private _gain: GainNode;

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: AudioPlayerOptions = {}) {
    super(options);

    // // https://stackoverflow.com/a/56770254/4906586
    // this._audioContext.onstatechange = () => {
    //   this.log(`AudioContext onstatechange: ${this._audioContext.state}`);
    // };
  }

  // --------------------------------------------------------------------------
  //    Methods: resources management
  // --------------------------------------------------------------------------
  async onUserInteraction() {
    await super.onUserInteraction();
    this._source = this._audioContext.createBufferSource();
  }

  // --------------------------------------------------------------------------
  //    Methods: audio playing
  // --------------------------------------------------------------------------
  protected async playAudioUrl(
    url: string,
    onNext: () => void,
    onError: (err: any) => void
  ): Promise<void> {
    try {
      // CORS mode must be triggered!
      const resp = await fetch(url, { mode: "cors" });

      // Something went wrong
      if (resp.status !== 200) {
        const respText = await resp.text();
        onError({ status: resp.status, message: respText });
        return;
      }

      // Extract received audio data
      const buffer = await resp.arrayBuffer();

      // Attempt to resume AudioContext
      // "interrupted" state is only for iOS
      if (
        this._audioContext.state === "suspended" ||
        (this._audioContext.state as any) === "interrupted"
      ) {
        this.log(`Audio#${this._currentlyPlayedIndex} resuming AudioContext`);
        await this._audioContext.resume();
      }

      // https://stackoverflow.com/a/52536753/4906586
      try {
        await this.doStop();

        const decodedAudioData = await this.decodeAudioData(buffer);

        this.decodeAudioSuccess(decodedAudioData, onNext);
      } catch (error) {
        console.log("caught decodeAudioData error", error);
        await this.fallbackDecode(buffer, onNext, onError);
      }
    } catch (err) {
      this.log(err);
      onError(err);
    }
  }

  doPause() {
    this._audioContext.suspend();
  }

  doResume() {
    this._audioContext.resume();
  }

  doStop() {
    if (this._source) {
      // Nulllify onended callback as it is called even when the playback is
      // interrupted
      this._source.onended = () => {
        console.log("audio source disconnected");
      };
      this._source.disconnect();
    }
  }
}
